import React from 'react';
import { Box, Flex, GridItem, Text } from '@chakra-ui/react';
import { ApiForecast } from 'src/api/queries';
import { useCurrency } from 'src/hooks/useCurrency';
import { useKpiMetricLabels } from 'src/hooks/useKpiMetricLabels';
import { useKpiUnit } from 'src/hooks/useKpiUnit';
import { attachCurrency } from 'src/utils/currency';

type ForecastSummaryProps = {
  forecast: ApiForecast;
};

export const ForecastSummary: React.FC<ForecastSummaryProps> = ({
  forecast,
}) => {
  const { code: currencyCode } = useCurrency();
  const { metricLabel } = useKpiMetricLabels();
  const { isEuro } = useKpiUnit();
  return (
    <GridItem colSpan={12}>
      <Flex gap={4} flexWrap="wrap">
        {forecast.scenarios.map((scenario) => {
          const totalPredictedSales = scenario.results.reduce(
            (sum, r) => sum + r.predictedSales,
            0,
          );
          const totalBaseline = scenario.results.reduce(
            (sum, r) => sum + r.baseline,
            0,
          );
          const totalSpend = scenario.results.reduce(
            (sum, r) => sum + r.totalSpend,
            0,
          );
          const incrementalSales = totalPredictedSales - totalBaseline;
          const roi = totalSpend > 0 ? incrementalSales / totalSpend : 0;

          return (
            <Box
              key={scenario.scenarioId}
              p={4}
              borderWidth="1px"
              borderRadius="md"
              borderColor="surface.border"
              flex="1"
              minW="250px">
              <Text
                fontWeight="bold"
                fontFamily="marketingPage"
                color="content.title"
                mb={3}>
                {scenario.scenarioName}
              </Text>
              <Flex justifyContent="space-between" mb={1}>
                <Text
                  fontSize="sm"
                  fontFamily="marketingPage"
                  color="content.muted">
                  Total Predicted {metricLabel}
                </Text>
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  fontFamily="marketingPage"
                  color="content.title">
                  {isEuro
                    ? attachCurrency(
                        totalPredictedSales.toLocaleString('en', {
                          maximumFractionDigits: 0,
                        }),
                        currencyCode,
                      )
                    : totalPredictedSales.toLocaleString('en', {
                        maximumFractionDigits: 0,
                      })}
                </Text>
              </Flex>
              <Flex justifyContent="space-between" mb={1}>
                <Text
                  fontSize="sm"
                  fontFamily="marketingPage"
                  color="content.muted">
                  Total Spend
                </Text>
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  fontFamily="marketingPage"
                  color="content.title">
                  {attachCurrency(
                    totalSpend.toLocaleString('en', {
                      maximumFractionDigits: 0,
                    }),
                    currencyCode,
                  )}
                </Text>
              </Flex>
              <Flex justifyContent="space-between">
                <Text
                  fontSize="sm"
                  fontFamily="marketingPage"
                  color="content.muted">
                  Incremental ROI
                </Text>
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  fontFamily="marketingPage"
                  color="content.title">
                  {roi.toFixed(2)}
                </Text>
              </Flex>
            </Box>
          );
        })}
      </Flex>
    </GridItem>
  );
};
